/**
 * Event Bus Implementation
 * Phase 2: System Modules Layer - Event System
 * 
 * Provides a centralized event handling system for the FreshCards subsystem.
 * Events can be subscribed, unsubscribed, and emitted with proper typing.
 */

import { EventHandler } from './types'

/**
 * Enhanced EventBus with better typing and debugging capabilities
 */
export class EventBus {
  private listeners: Map<string, Set<EventHandler>> = new Map()
  private eventHistory: Array<{
    event: string
    data: any
    timestamp: Date
  }> = []
  private maxHistorySize = 1000

  /**
   * Subscribe to an event
   * @param event - The event name to subscribe to
   * @param handler - The event handler function
   * @returns A unique subscription ID that can be used for unsubscription
   */
  on(event: string, handler: EventHandler): string {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(handler)
    
    // Return a unique ID for this subscription
    return `${event}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Subscribe to an event with automatic unsubscription after first execution
   * @param event - The event name to subscribe to
   * @param handler - The event handler function
   * @returns A unique subscription ID that can be used for unsubscription
   */
  once(event: string, handler: EventHandler): string {
    const onceHandler: EventHandler = (data) => {
      handler(data)
      this.off(event, onceHandler)
    }
    return this.on(event, onceHandler)
  }

  /**
   * Unsubscribe from an event
   * @param event - The event name to unsubscribe from
   * @param handler - The specific handler to remove, or omit to remove all handlers for the event
   */
  off(event: string, handler?: EventHandler): void {
    const listeners = this.listeners.get(event)
    if (listeners) {
      if (handler) {
        listeners.delete(handler)
      } else {
        listeners.clear()
      }
      
      if (listeners.size === 0) {
        this.listeners.delete(event)
      }
    }
  }

  /**
   * Emit an event to all subscribers
   * @param event - The event name to emit
   * @param data - The data to pass to event handlers
   */
  emit(event: string, data: any): void {
    const listeners = this.listeners.get(event)
    if (listeners) {
      // Create a copy to avoid issues if handlers modify the set
      const listenersCopy = new Set(listeners)
      
      // Add to event history for debugging
      this.addToHistory(event, data)
      
      // Call all handlers
      listenersCopy.forEach(handler => {
        try {
          handler(data)
        } catch (error) {
          console.error(`Error in event handler for '${event}':`, error)
          // Emit error event for debugging
          this.emit('event:error', {
            event,
            error,
            data
          })
        }
      })
    }
  }

  /**
   * Check if there are any listeners for an event
   * @param event - The event name to check
   * @returns True if there are listeners, false otherwise
   */
  hasListeners(event: string): boolean {
    return this.listeners.has(event) && this.listeners.get(event)!.size > 0
  }

  /**
   * Get the number of listeners for an event
   * @param event - The event name to check
   * @returns The number of listeners
   */
  getListenerCount(event: string): number {
    return this.listeners.get(event)?.size || 0
  }

  /**
   * Get all events that have listeners
   * @returns An array of event names
   */
  getActiveEvents(): string[] {
    return Array.from(this.listeners.keys())
  }

  /**
   * Clear all listeners for all events
   */
  clearAll(): void {
    this.listeners.clear()
    this.eventHistory = []
  }

  /**
   * Clear listeners for a specific event
   * @param event - The event name to clear
   */
  clearEvent(event: string): void {
    this.listeners.delete(event)
  }

  /**
   * Get event history for debugging
   * @param limit - Maximum number of history entries to return
   * @returns Array of historical events
   */
  getEventHistory(limit?: number): Array<{
    event: string
    data: any
    timestamp: Date
  }> {
    const history = [...this.eventHistory]
    return limit ? history.slice(-limit) : history
  }

  /**
   * Add event to history
   * @param event - The event name
   * @param data - The event data
   */
  private addToHistory(event: string, data: any): void {
    this.eventHistory.push({
      event,
      data,
      timestamp: new Date()
    })
    
    // Limit history size
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory = this.eventHistory.slice(-this.maxHistorySize)
    }
  }

  /**
   * Wait for a specific event to be emitted
   * @param event - The event name to wait for
   * @param timeout - Optional timeout in milliseconds
   * @returns Promise that resolves with the event data
   */
  waitFor(event: string, timeout?: number): Promise<any> {
    return new Promise((resolve, reject) => {
      let timeoutId: NodeJS.Timeout
      
      const handler: EventHandler = (data) => {
        this.off(event, handler)
        if (timeoutId) clearTimeout(timeoutId)
        resolve(data)
      }
      
      this.on(event, handler)
      
      if (timeout) {
        timeoutId = setTimeout(() => {
          this.off(event, handler)
          reject(new Error(`Timeout waiting for event: ${event}`))
        }, timeout)
      }
    })
  }

  /**
   * Emit an event and wait for a response event
   * @param event - The event name to emit
   * @param data - The data to pass to event handlers
   * @param responseEvent - The response event name to wait for
   * @param timeout - Optional timeout in milliseconds
   * @returns Promise that resolves with the response data
   */
  emitAndWait(
    event: string, 
    data: any, 
    responseEvent: string, 
    timeout: number = 5000
  ): Promise<any> {
    this.emit(event, data)
    return this.waitFor(responseEvent, timeout)
  }
}

// Create a singleton instance for use across the application
export const eventBus = new EventBus()

// Export convenience methods for direct use
export const { on, once, off, emit, hasListeners, getListenerCount, getActiveEvents, clearAll, clearEvent, getEventHistory, waitFor, emitAndWait } = eventBus